import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useGetUserDetailsQuery } from '../redux/slices/auth/authApiSlice';
import oktaService from '@/services/OktaService';
import { clearAPICache } from '@/utils/cacheUtils';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store';
import { getMockUser, isMockAuthenticated } from '@/services/mockAuth';

export function useAuth() {
    const navigate = useNavigate();
    const location = useLocation();
    const [isCheckingOkta, setIsCheckingOkta] = useState(true);
    const [oktaAuthenticated, setOktaAuthenticated] = useState(false);
    const isOfflineMode = useSelector((state: RootState) => state.offline.isOfflineMode);
    const mockUser = getMockUser();
    const mockAuthenticated = isMockAuthenticated();

    // Only fetch user details if we've completed the Okta check and not in offline mode
    const { data, error, isLoading, refetch, isError } = useGetUserDetailsQuery('', {
        // Skip the query if we're checking Okta or in offline mode
        skip: isCheckingOkta || isOfflineMode || mockAuthenticated,
        // Don't cache the result
        refetchOnMountOrArgChange: true,
    });

    // Check Okta authentication status (skip if in offline mode)
    useEffect(() => {
        const checkOktaAuth = async () => {
            try {
                // If in offline mode, skip Okta check entirely
                if (isOfflineMode || mockAuthenticated) {
                    setIsCheckingOkta(false);
                    return;
                }

                // Clear API cache before checking auth status
                await clearAPICache();

                const isAuth = await oktaService.isAuthenticated();
                setOktaAuthenticated(isAuth);
                setIsCheckingOkta(false);
            } catch (error) {
                console.error('Error checking Okta auth:', error);
                setOktaAuthenticated(false);
                setIsCheckingOkta(false);
            }
        };

        checkOktaAuth();
    }, [isOfflineMode, mockAuthenticated]);

    // Handle API errors and redirect to login - but only if not in offline mode
    useEffect(() => {
        if (isError && !isOfflineMode && !location.pathname.includes('/offline') && !location.pathname.includes('/login')) {
            // Clear API cache before redirecting
            clearAPICache().then(() => {
                navigate('/login?forceClear=true');
            });
        }
    }, [error, navigate, location.pathname, isError, isOfflineMode]);

    // Determine if user is authenticated
    // In offline mode, we consider the user "authenticated" for the app to work
    const isAuthenticated = isOfflineMode || mockAuthenticated || (!isLoading && !!data && !error) || oktaAuthenticated;
    const isStillLoading = !isOfflineMode && !mockAuthenticated && (isLoading || isCheckingOkta);

    return {
        isAuthenticated,
        isLoading: isStillLoading,
        user: mockUser || data?.user,
        refetch
    };
} 
